import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  Button,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { QR, QrModalHeader, qrDialogPaperSx } from "./qrUi";
import Slide from "@mui/material/Slide";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CheckIcon from "@mui/icons-material/Check";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ApartmentIcon from "@mui/icons-material/Apartment";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CircularLoader from "../../../components/loaders/circularLoader";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import DownloadFile from "../../../components/downloadMediaFiles/downloadMediaFile";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import { useSelector } from "react-redux";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// One "label — value" row in the details list
const DetailRow = ({ icon, label, children }) => (
  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ py: 1, borderBottom: `1px dashed ${QR.border}` }}>
    <Box sx={{ color: "#94a3b8", display: "flex", "& svg": { fontSize: 18 } }}>{icon}</Box>
    <Typography variant="body2" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1, textAlign: "right" }}>{children}</Box>
  </Stack>
);

// Scanner-style corner accents around the QR image
const corner = (pos) => ({
  position: "absolute",
  width: 22,
  height: 22,
  borderColor: QR.main,
  borderStyle: "solid",
  borderWidth: 0,
  ...(pos.includes("t") ? { top: -1, borderTopWidth: 3 } : { bottom: -1, borderBottomWidth: 3 }),
  ...(pos.includes("l") ? { left: -1, borderLeftWidth: 3 } : { right: -1, borderRightWidth: 3 }),
  borderRadius:
    pos === "tl" ? "10px 0 0 0" : pos === "tr" ? "0 10px 0 0" : pos === "bl" ? "0 0 0 10px" : "0 0 10px 0",
});

export default function ViewQrCodeModal({ show, handleClose, rowData }) {
  const { user } = useSelector((state) => state.authentication);

  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (show) {
      setIsLoading(true);
      setCopied(false);
      const timer = setTimeout(() => setIsLoading(false), 800);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const whatsappUrl = rowData?.url && user?.sender_mobile ? `https://wa.me/${user.sender_mobile}?text=${rowData.url}` : "";

  const handleCopy = async () => {
    if (rowData?.url && user?.sender_mobile) {
      const whatsappUrl = `https://wa.me/${user.sender_mobile}?text=${rowData.url}`;
      try {
        await navigator.clipboard.writeText(whatsappUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        setSnackbar({
          open: true,
          message: "QR Code URL copied successfully",
          severity: "success",
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to copy URL",
          severity: "error",
        });
      }
    }
  };

  return (
    <>
      <Dialog
        open={show}
        onClose={handleClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        keepMounted
        PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: 640 } }}
      >
        {/* Header */}
        <QrModalHeader
          icon={<QrCode2Icon />}
          title="QR Code"
          subtitle={rowData?.title}
          onClose={handleClose}
        />

        {/* Content */}
        <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: "#fff" }}>
          {isLoading ? (
            <CircularLoader />
          ) : rowData?.qr_code_path ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ xs: "center", sm: "stretch" }}>
              {/* ---------- Left: QR with scanner corners ---------- */}
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1.25}
                sx={{ flexShrink: 0, p: 2, borderRadius: 3, bgcolor: "#f8fafc", border: `1px solid ${QR.border}` }}
              >
                <Box sx={{ position: "relative", p: 1.25, bgcolor: "#fff", borderRadius: 2.5, boxShadow: "0 10px 24px -14px rgba(15,23,42,0.35)" }}>
                  {["tl", "tr", "bl", "br"].map((pos) => (
                    <Box key={pos} sx={corner(pos)} />
                  ))}
                  <img
                    src={rowData.qr_code_path}
                    alt="QR Code"
                    style={{ width: 190, height: 190, display: "block", borderRadius: 6, objectFit: "contain" }}
                  />
                </Box>
                {/* Key below the QR */}
                {rowData.url && (
                  <Box
                    component="span"
                    sx={{
                      mt: 0.5,
                      px: 1.25,
                      py: 0.35,
                      borderRadius: 1.5,
                      fontFamily: "monospace",
                      fontSize: 12.5,
                      color: "#334155",
                      bgcolor: "#fff",
                      border: "1px solid #e2e8f0",
                      maxWidth: 212,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={rowData.url}
                  >
                    Key: <b>{rowData.url}</b>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  Scan with a phone camera
                </Typography>
              </Stack>

              {/* ---------- Right: details + actions ---------- */}
              <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                {/* "QR Code Name: rr" on one line */}
                <Typography noWrap title={rowData.title} sx={{ fontSize: 14.5, lineHeight: 1.4 }}>
                  <Box component="span" sx={{ color: "#64748b", fontWeight: 500 }}>
                    QR Code Name:
                  </Box>{" "}
                  <Box component="span" sx={{ color: "#0f172a", fontWeight: 800 }}>
                    {rowData.title}
                  </Box>
                </Typography>

                {/* Details */}
                <Box sx={{ mt: 1.5 }}>
                  <DetailRow icon={<ApartmentIcon />} label="Branch">
                    <Typography variant="body2" fontWeight={600} color="#0f172a" noWrap title={rowData.branch_department_name}>
                      {rowData.branch_department_name || "-"}
                    </Typography>
                  </DetailRow>
                  <DetailRow icon={<LocalOfferOutlinedIcon />} label="Tag">
                    {rowData.tag_name ? (
                      <Box
                        component="span"
                        sx={{ px: 1, py: 0.25, borderRadius: 5, fontSize: 12, fontWeight: 700, color: QR.purple, bgcolor: `${QR.purple}12`, border: `1px solid ${QR.purple}26` }}
                      >
                        {rowData.tag_name}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </DetailRow>
                  <DetailRow icon={<GroupsOutlinedIcon />} label="Leads">
                    <Typography variant="body2" fontWeight={800} color="#0f172a">
                      {Number(rowData.overall_count) || 0}
                      <Typography component="span" variant="caption" color="text.secondary" fontWeight={500} sx={{ ml: 0.75 }}>
                        ({Number(rowData.last30days_count) || 0} in 30 days)
                      </Typography>
                    </Typography>
                  </DetailRow>
                  <DetailRow icon={<EventOutlinedIcon />} label="Created">
                    <Typography variant="body2" fontWeight={600} color="#0f172a">
                      {rowData.inserted_time ? formatDate(rowData.inserted_time) : "-"}
                    </Typography>
                  </DetailRow>
                </Box>

                {/* WhatsApp link with inline copy */}
                {whatsappUrl && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 1.75, pl: 1.25, pr: 0.5, py: 0.5, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}
                  >
                    <WhatsAppIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0, color: "#166534", fontFamily: "monospace" }} noWrap title={whatsappUrl}>
                      {whatsappUrl}
                    </Typography>
                    <Tooltip title={copied ? "Copied" : "Copy link"}>
                      <IconButton size="small" onClick={handleCopy} aria-label="copy QR code URL" sx={{ color: "#16a34a" }}>
                        {copied ? <CheckIcon sx={{ fontSize: 17 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}

                <Box sx={{ flex: 1, minHeight: 16 }} />

                {/* Actions */}
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                    onClick={handleCopy}
                    fullWidth
                    sx={{
                      height: 38,
                      fontSize: 13.5,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 3,
                      color: QR.blue,
                      borderColor: `${QR.blue}66`,
                      bgcolor: "#fff",
                      "&:hover": { borderColor: QR.blue, bgcolor: `${QR.blue}0d` },
                    }}
                    aria-label="copy QR code URL"
                  >
                    {copied ? "Copied" : "Copy URL"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => DownloadFile(rowData.qr_code_path, rowData.title)}
                    fullWidth
                    sx={{
                      height: 38,
                      fontSize: 13.5,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 3,
                      boxShadow: "none",
                      background: `linear-gradient(135deg, ${QR.blue}, #3b82f6)`,
                      "&:hover": { boxShadow: `0 8px 18px -8px ${QR.blue}` },
                    }}
                    aria-label="download QR code"
                  >
                    Download
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={1} p={2} sx={{ color: "#94a3b8" }}>
              <QrCode2Icon sx={{ fontSize: 48 }} />
              <Typography variant="body2">No QR code available</Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for feedback */}
      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}
